import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Coins, LogOut, CheckCircle2 } from 'lucide-react';
import { formatINR } from '@vj/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { Button } from './ui/Button.jsx';

/**
 * The daily rate gate.
 *
 * Wraps the entire authenticated app. Until every active purity has a rate
 * entered for today, this is the only screen reachable — no dashboard, no
 * customers, nothing. That is a deliberate, literal reading of the owner's
 * instruction ("MUST enter rate then only proceed with the app"), not a
 * softened version scoped to billing screens only.
 *
 * Rows are never overwritten (see the metal_rates schema comment) — submitting
 * again today inserts a correction, it doesn't edit the earlier entry.
 */
export function RateGate({ children }) {
  const { data, isLoading } = useQuery({
    queryKey: ['rates', 'today'],
    queryFn: () => api.get('/rates/today'),
    // The gate is the one screen where staleness would be actively wrong —
    // if it opened, it should stay open without a manual refetch.
    staleTime: 0,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg">
        <Loader2 size={24} className="animate-spin text-muted" aria-hidden="true" />
      </div>
    );
  }

  if (!data?.gateOpen) {
    return <RateEntryScreen snapshot={data} />;
  }

  return children;
}

function RateEntryScreen({ snapshot }) {
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const purities = snapshot?.purities ?? [];

  const [values, setValues] = useState(() => Object.fromEntries(purities.map((p) => [p.purityId, ''])));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const grouped = purities.reduce((acc, p) => {
    (acc[p.metal] ??= []).push(p);
    return acc;
  }, {});

  const allFilled = purities.every((p) => p.ratePerGram !== null || Number(values[p.purityId]) > 0);
  const pending = purities.filter((p) => p.ratePerGram === null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const rates = pending
        .map((p) => ({ purityId: p.purityId, ratePerGram: Number(values[p.purityId]) }))
        .filter((r) => r.ratePerGram > 0);

      if (rates.length !== pending.length) {
        setError("Enter today's rate for every metal and purity below.");
        setSubmitting(false);
        return;
      }

      await api.post('/rates/today', { rates });
      await queryClient.invalidateQueries({ queryKey: ['rates', 'today'] });
    } catch {
      setError('Could not save the rates. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-soft text-primary">
            <Coins size={22} aria-hidden="true" />
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Enter today&rsquo;s rate</h1>
          <p className="mt-1 text-sm text-muted">
            Required once each day before the app can be used.
          </p>
        </div>

        <form onSubmit={onSubmit} className="card flex flex-col gap-5 p-6">
          {Object.entries(grouped).map(([metal, list]) => (
            <div key={metal} className="flex flex-col gap-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">{metal}</h2>
              {list.map((p) => (
                <div key={p.purityId} className="flex items-center gap-3">
                  <label htmlFor={`rate-${p.purityId}`} className="w-16 shrink-0 text-sm font-medium">
                    {p.label}
                  </label>
                  {p.ratePerGram !== null ? (
                    <div className="flex h-touch flex-1 items-center gap-2 rounded-card border border-success/30 bg-success/10 px-3.5 text-sm text-success">
                      <CheckCircle2 size={16} aria-hidden="true" />
                      {formatINR(p.ratePerGram)} / g — already set today
                    </div>
                  ) : (
                    <div className="relative flex-1">
                      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted">
                        ₹
                      </span>
                      <input
                        id={`rate-${p.purityId}`}
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        required
                        placeholder="0.00"
                        value={values[p.purityId]}
                        onChange={(e) => setValues((v) => ({ ...v, [p.purityId]: e.target.value }))}
                        className="h-touch w-full rounded-card border border-border bg-surface pl-7 pr-3 text-content"
                      />
                      <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-muted">
                        /g
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {error ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" size="lg" loading={submitting} disabled={pending.length === 0 && !allFilled} className="w-full">
            {pending.length === 0 ? 'Continue' : "Save today's rates"}
          </Button>
        </form>

        <button
          type="button"
          onClick={logout}
          className="mx-auto mt-5 flex h-10 items-center gap-2 text-sm text-muted hover:text-danger"
        >
          <LogOut size={15} aria-hidden="true" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default RateGate;
